import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { LoginServerService } from '../../../authentication/services/login-server.service';
import { isPlatformBrowser } from '@angular/common';
import { UserService } from '../../../../pages/administrateur/service/user.service';
import { ProfilService } from '../../../../pages/administrateur/service/profil.service';
import { TranslateService } from '@ngx-translate/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { ProfileUpdateService } from '../../../../pages/administrateur/service/profile-update.service';
import { Subscription, filter } from 'rxjs';
import { MessagerieService } from '../../../../pages/administrateur/service/messagerie.service';
import { AbonnementApiService } from '../../../../pages/parent/services/abonnement-api.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css']
})
export class SidebarComponent implements OnInit, OnDestroy {
  userEmail: string | null = null;
  role: string | null = null;
  profileImageUrl: string = '../../../../assets/img/Admin.png';
  private userIdKey = parseInt(localStorage.getItem('userId') as string, 10);
  userProfile: any;
  messageCount: number = 0;
  unpaidChildrenCount: number = 0;
  isCollapsed: boolean = false;
  private profileSub?: Subscription;
  private msgSub?: Subscription;
  private refreshSub?: Subscription;

  constructor(
    public router: Router,
    private authProvider: LoginServerService,
    private profilService: ProfilService,
    public translate: TranslateService,
    private notification: NzNotificationService,
    private userService: UserService,
    private profileUpdateService: ProfileUpdateService,
    private messagerieService: MessagerieService,
    private abonnementService: AbonnementApiService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.loadUserEmail();
      this.loadRole();
      this.loadProfilePhoto();
      this.loadProfile();
    }
    // Load unread message count
    this.loadMessageCount();

    // Load unpaid children count for parent
    if (this.isParent()) {
      this.loadUnpaidChildrenCount();
    }

    // Subscribe to new messages for updating the badge
    this.msgSub = this.messagerieService.messages$.pipe(filter(m => m !== null)).subscribe(() => {
      this.loadMessageCount();
    });

    // Subscribe to explicit refresh triggers (e.g. after marking conversation as read)
    this.refreshSub = this.messagerieService.unreadCountRefreshed$.subscribe(() => {
      this.loadMessageCount();
    });

    // Subscribe to real-time profile changes from ProfilComponent
    this.profileSub = this.profileUpdateService.profileUpdated$.subscribe(event => {
      if (this.userProfile) {
        this.userProfile = { ...this.userProfile, prenom: event.prenom, nom: event.nom };
      } else {
        this.userProfile = { prenom: event.prenom, nom: event.nom };
      }
      if (event.photo) {
        this.profileImageUrl = event.photo;
      }
    });
  }

  ngOnDestroy(): void {
    this.profileSub?.unsubscribe();
    this.msgSub?.unsubscribe();
    this.refreshSub?.unsubscribe();
  }

  loadProfile() {
    this.profilService.getProfilById(this.userIdKey).subscribe({
      next: (data) => {
        this.userProfile = data;
      },
      error: () => {},
    });
  }

  private loadMessageCount(): void {
    const uid = parseInt(localStorage.getItem('userId') as string, 10);
    if (isNaN(uid)) return;
    this.messagerieService.getUnreadMessageCount(uid).subscribe({
      next: (count) => { this.messageCount = count; },
      error: () => { /* silent */ }
    });
  }

  resetMessageCount(): void {
    this.messageCount = 0;
  }

  private loadUnpaidChildrenCount(): void {
    this.abonnementService.getAbonnementStatus().subscribe({
      next: (res) => {
        if (res.statut === 'EXPIRED' || res.statut === 'TRIAL') {
          this.unpaidChildrenCount = res.nombreEnfants || 0;
        } else {
          this.unpaidChildrenCount = 0;
        }
      },
      error: () => { this.unpaidChildrenCount = 0; }
    });
  }

  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  // Role checking methods
  isDirecteur(): boolean {
    const userRole = localStorage.getItem('_sid');
    return userRole === 'Directeur';
  }

  isParent(): boolean {
    const userRole = localStorage.getItem('_sid');
    return userRole === 'Parent';
  }

  isFormateur(): boolean {
    const userRole = localStorage.getItem('_sid');
    return userRole === 'Educateur';
  }

  // Method to load the user's email from localStorage
  private loadUserEmail(): void {
    const userEmail = localStorage.getItem('name');
    this.userEmail = userEmail;
  }

  // Method to load the user's role from localStorage
  private loadRole(): void {
    const role = localStorage.getItem('_sid');
    this.role = role;
  }

  // Method to get translated role key
  getRoleTranslationKey(): string {
    if (this.role === 'Directeur') {
      return 'DASHBOARD.ROLES.DIRECTOR';
    } else if (this.role === 'Educateur') {
      return 'DASHBOARD.ROLES.EDUCATOR';
    } else if (this.role === 'Parent') {
      return 'DASHBOARD.PARENT_ROLE';
    }
    return this.role || '';
  }

  // Method to load the profile image from backend or use the default one
  private loadProfilePhoto(): void {
    if (isPlatformBrowser(this.platformId)) {
      const savedImage = localStorage.getItem('profileImage');
      if (savedImage) {
        this.profileImageUrl = savedImage; // Use the saved photo from localStorage
      } else {
        // If no image is saved in localStorage, check with the server
        this.userService.getPhotoByUserId(this.userIdKey).subscribe({
          next: (photoUrl: string) => {
            this.profileImageUrl = photoUrl || this.getDefaultAvatar(); // Set the fetched photo URL or default
            localStorage.setItem('profileImage', this.profileImageUrl); // Cache the photo URL in localStorage
          },
          error: () => {
            this.profileImageUrl = this.getDefaultAvatar();
          },
        });
      }
    }
  }

  // Method to handle logout
  handleLogout(): void {
    this.authProvider.logout();
  }

  getDefaultAvatar(): string {
    const role = localStorage.getItem('_sid');
    switch (role) {
      case 'Parent': return '../../../../assets/img/parent.png';
      case 'Educateur': return '../../../../assets/img/formateur1.png';
      case 'Directeur': return '../../../../assets/img/directeur.png';
      default: return '../../../../assets/img/Admin.png';
    }
  }

  // Return the correct document management route based on role
  getDocumentRoute(): string[] {
    if (this.isDirecteur()) {
      return ['/beekids/Document'];
    }
    // Default to enseignant route for formateurs/educateurs
    return ['/beekids/educateur-docs'];
  }

  // Programmatic navigation to bypass any routerLink issues
  navigateToDocuments(): void {
    const route = this.isDirecteur() ? '/beekids/Document' : '/beekids/educateur-docs';
    this.router.navigate([route]);
  }

  navigateToConges(): void {
    this.router.navigate(['/beekids/educateur-docs/conges']);
  }
}
